import { useCallback, useState, type Dispatch, type SetStateAction } from "react";
import { fieldTypeOptions } from "../GenericField/GenericField";

/**
 * Props returned for a given field key. Only `input` (the field type) is read
 * here; other props (label, options, etc.) are present at runtime but unused
 * by this hook.
 */
interface FieldProps {
  input?: string;
  [key: string]: unknown;
}

export type GetPropsFromField = (key: string) => FieldProps | undefined;

/**
 * A single dynamic-mapping entry, e.g. `{ field: "summary", value: { text } }`.
 */
interface DynamicMappingFieldValue {
  field?: string;
  // The value shape depends on the target field's input type (string,
  // `{ text }`, `{ html }`, etc.), so it is narrowed at the usage site.
  value?: unknown;
}

interface MutationEntry {
  key: string;
  value: unknown;
}

/**
 * Raw form data keyed by field name. Values are heterogeneous (string,
 * `{ text }`, `{ html }`, dynamic-mapping arrays, mapping records), so they are
 * narrowed at the usage site.
 */
type MutationFormData = Record<string, unknown>;

interface UseTicketEndpointMutationDataReturn {
  getPropsFromField: GetPropsFromField | undefined;
  setGetPropsFromField: Dispatch<
    SetStateAction<GetPropsFromField | undefined>
  >;
  getMutationArray: (data?: MutationFormData) => MutationEntry[];
}

const useTicketEndpointMutationData =
  (): UseTicketEndpointMutationDataReturn => {
    // Lazy initializer returns undefined (original runtime: `() => {}`),
    // explicitly typed so the state isn't inferred as `void`.
    const [getPropsFromField, setGetPropsFromField] = useState<
      GetPropsFromField | undefined
    >((): GetPropsFromField | undefined => undefined);

    const getMutationArray = useCallback(
      (data?: MutationFormData): MutationEntry[] => {
        const mutationArray: MutationEntry[] = [];
        if (!!data && Object.keys(data).length) {
          Object.keys(data).forEach((key) => {
            if (!!data[key] || data[key] === "") {
              if (
                getPropsFromField!(key)?.input ===
                fieldTypeOptions.DYNAMIC_MAPPING
              ) {
                const dynamicMappingFields = data[
                  key
                ] as DynamicMappingFieldValue[] | undefined;
                dynamicMappingFields?.forEach((dynamicMappingField) => {
                  if (!!dynamicMappingField?.value) {
                    try {
                      let newValue = dynamicMappingField?.value as
                        | { text?: unknown; html?: unknown }
                        | undefined;
                      const fieldProps = getPropsFromField!(
                        dynamicMappingField?.field as string
                      );
                      const fieldInput = fieldProps?.input;

                      if (
                        fieldInput ===
                          fieldTypeOptions.AUTOCOMPLETE_ONLY_TEXTAREA ||
                        fieldInput === fieldTypeOptions.INLINE_AUTOCOMPLETE
                      ) {
                        newValue = newValue?.text as typeof newValue;
                      }

                      if (fieldInput === fieldTypeOptions.HTML_TEXT_AREA) {
                        newValue = newValue?.html as typeof newValue;
                      }

                      mutationArray.push({
                        key: dynamicMappingField?.field as string,
                        value: newValue,
                      });
                    } catch (e) {
                      mutationArray.push({
                        key: dynamicMappingField?.field as string,
                        value: dynamicMappingField?.value,
                      });
                    }
                  }
                });
              } else if (
                getPropsFromField!(key)?.input === fieldTypeOptions.MAPPING ||
                getPropsFromField!(key)?.input ===
                  fieldTypeOptions.UNIQUE_MAPPING
              ) {
                const mappingField = data[key] as Record<string, unknown>;
                Object.keys(mappingField).forEach((mappingFieldProp) => {
                  if (!mappingField[mappingFieldProp]) {
                    delete mappingField?.[mappingFieldProp];
                  }
                });
                mutationArray.push({
                  key,
                  value: mappingField,
                });
              } else if (
                getPropsFromField!(key)?.input ===
                  fieldTypeOptions.AUTOCOMPLETE_ONLY_TEXTAREA ||
                getPropsFromField!(key)?.input ===
                  fieldTypeOptions.INLINE_AUTOCOMPLETE ||
                getPropsFromField!(key)?.input ===
                  fieldTypeOptions.HTML_TEXT_AREA
              ) {
                let newValue = data[key] as
                  | { text?: unknown; html?: unknown }
                  | undefined;

                if (
                  getPropsFromField!(key)?.input ===
                    fieldTypeOptions.AUTOCOMPLETE_ONLY_TEXTAREA ||
                  getPropsFromField!(key)?.input ===
                    fieldTypeOptions.INLINE_AUTOCOMPLETE
                ) {
                  newValue = newValue?.text as typeof newValue;
                }

                if (
                  getPropsFromField!(key)?.input ===
                  fieldTypeOptions.HTML_TEXT_AREA
                ) {
                  newValue = newValue?.html as typeof newValue;
                }

                mutationArray.push({
                  key: key,
                  value: newValue,
                });
              } else {
                mutationArray.push({
                  key: key,
                  value: data[key],
                });
              }
            }
          });
        }
        return mutationArray;
      },
      [getPropsFromField]
    );

    return {
      getPropsFromField,
      setGetPropsFromField,
      getMutationArray,
    };
  };

export default useTicketEndpointMutationData;
